/**
 * Normalize enrichment preview relationship rows before POST /api/relationships.
 * Incoming-on-application types must have the peer (org/initiative/platform) as source.
 * Compound LLM/orchestrator types are mapped to canonical types expected by tenant rules + UI.
 */

/** Compound enrichment type → canonical type stored in DB (matches relationship rules). */
export const ENRICHMENT_COMPOUND_TO_CANONICAL = {
  "organization uses application": "uses",
  "initiative affects application": "affects",
  "application runs on platform": "runs",
  "application supports business-capability": "supports",
  "application supports business-context": "supports",
  "application accesses data-object": "accesses",
  "application uses technology-component": "uses",
};

const INCOMING_ON_APPLICATION_TYPES = new Set([
  "organization uses application",
  "initiative affects application",
]);

const PLATFORM_INCOMING_TYPE = "application runs on platform";

export function canonicalRelationshipTypeForPersist(relationshipType) {
  const rt = String(relationshipType || "").trim();
  if (!rt) return rt;
  return ENRICHMENT_COMPOUND_TO_CANONICAL[rt] || rt;
}

export function normalizeEnrichmentRelationshipForPersist(rel, anchorApplicationId) {
  if (!rel || typeof rel !== "object") return rel;
  const anchor = String(anchorApplicationId || "").trim();
  if (!anchor) return rel;

  const rt = String(rel.relationship_type || rel.type || "").trim();
  let sourceId = String(rel.source_entity_id || "").trim();
  let targetId = String(rel.target_entity_id || "").trim();

  let out = { ...rel, relationship_type: rt };

  if (INCOMING_ON_APPLICATION_TYPES.has(rt)) {
    if (sourceId === anchor && targetId) {
      out = {
        ...out,
        source_entity_id: targetId,
        target_entity_id: anchor,
      };
    } else {
      out = { ...out, source_entity_id: sourceId, target_entity_id: targetId };
    }
  } else if (rt === PLATFORM_INCOMING_TYPE) {
    if (sourceId === anchor && targetId) {
      out = {
        ...out,
        source_entity_id: targetId,
        target_entity_id: anchor,
      };
    } else {
      out = { ...out, source_entity_id: sourceId, target_entity_id: targetId };
    }
  }

  const canonical = canonicalRelationshipTypeForPersist(rt);
  return {
    ...out,
    relationship_type: canonical,
    type: canonical,
  };
}

/** Section keys that list incoming peers on an application (org / initiative / platform). */
export const ENRICHMENT_INCOMING_COMPOUND_SECTIONS = {
  organization_uses_application_reverse: {
    compoundTypes: new Set(["organization uses application"]),
    canonicalTypes: new Set(["uses", "utilizes", "consumes"]),
    peerType: "organization",
  },
  initiative_affects_application_reverse: {
    compoundTypes: new Set(["initiative affects application"]),
    canonicalTypes: new Set(["affects", "impacts", "realizes", "builds"]),
    peerType: "initiative",
  },
  platform_runs_application_reverse: {
    compoundTypes: new Set(["application runs on platform", "runs on platform"]),
    canonicalTypes: new Set(["runs"]),
    peerType: "platform",
  },
};

export function enrichmentIncomingCompoundRelMatches(rel, sectionKey, currentIdNorm, peerLikeFn) {
  const cfg = ENRICHMENT_INCOMING_COMPOUND_SECTIONS[sectionKey];
  if (!cfg || !currentIdNorm) return false;
  const rt = String(rel?.type || rel?.relationship_type || "")
    .trim()
    .toLowerCase();
  if (!cfg.compoundTypes.has(rt) && !cfg.canonicalTypes.has(rt)) return false;

  const sid = String(rel?.source_entity_id || "").trim().toLowerCase();
  const tid = String(rel?.target_entity_id || "").trim().toLowerCase();
  const cid = String(currentIdNorm).trim().toLowerCase();

  if (tid === cid) {
    return peerLikeFn(rel?.source_entity_id, rel?.source_entity_type);
  }
  if (sid === cid) {
    return peerLikeFn(rel?.target_entity_id, rel?.target_entity_type);
  }
  return false;
}
